<template>
  <page content-class="admin-campaign-list">
    <div class="d-flex justify-content-between align-items-center">
      <h3 class="py-3 m-0"><strong>{{ $t('campaign-admin.titles.campaigns') }}</strong></h3>
      <b-button @click="createNewCampaign" variant="primary" pill>{{ $t('campaign-admin.titles.new-campaign') }}</b-button>
    </div>
    <b-row class="pb-3 border-bottom mb-3">
      <b-col cols="12" md="3" class="mt-3">
          <b-form-input v-model="filterWhere.title_like" placeholder="Search title..." />
      </b-col>
      <b-col cols="12" md="3" class="mt-3">
          <b-form-select v-model="filterWhere.status" :options="statusOptions" />
      </b-col>
      <b-col cols="12" md="3" class="mt-3">
          <b-form-select v-model="filterWhere.template" :options="templateOptions" />
      </b-col>
      <b-col cols="12" md="3" class="mt-3">
          <b-form-select v-model="filterOrder" :options="sortOptions" />
      </b-col>
    </b-row>
    <div v-if="campaignsConnection && Array.isArray(campaignsConnection.edges)" class="mt-2">
      <b-card
        v-for="{ entry: campaign } in campaignsConnection.edges"
        :key="campaign.id"
        :campaign="campaign"
        class="my-2"
        style="border-radius: 3px; border: 1px solid #e2e8f0;"
        no-body
      >
        <b-row class="p-3">
          <b-col cols="12" class="d-flex justify-content-between align-items-center pb-3">
            <div class="d-flex justify-content-start align-items-center">
              <b-img v-if="campaign.branding && campaign.branding.colour" blank :blank-color="campaign.branding.colour"
                class="mr-3" height="18px" width="18px" rounded/>
              <h4 class="m-0 font-weight-bold" style="font-size: 16px;">
                <router-link :to="{ name: initialRouteName, params: { campaignID: campaign.id } }">{{ campaign.title }}</router-link>
              </h4>
            </div>
            <div>
              <span v-if="campaign.preferences && campaign.preferences.isTemplate"
                class="status-label mr-2" :style="{ backgroundColor: '#eab308' }">
                  Template
              </span>
              <span v-if="campaign.preferences && campaign.preferences.isGlobalTemplate"
                class="status-label" :style="{ backgroundColor: '#2563eb' }">
                  Real Links Template
              </span>
              <span
                v-if="campaign.status && statusMap[campaign.status] && !campaign.isDisabled"
                class="status-label"
                :style="{ backgroundColor: statusMap[campaign.status].colour, cursor: 'pointer' }"
                @click="$bvModal.show(`status-modal-${campaign.id}`)"
              >
                  {{ statusMap[campaign.status].label }}<font-awesome-icon class="ml-2" icon="caret-down" />
              </span>
            </div>
          </b-col>
          <b-col cols="12" class="d-flex justify-content-between align-items-center">
            <div v-if="!campaign.isGlobalTemplate" class="d-flex justify-content-start align-items-center">
              <span>
                <template v-if="campaign.startDate">{{ campaign.startDate | formatDate('Do MMM YYYY, HH:mm') }}</template>
                <span v-else class="font-italic">{{ $t('general.none') }}</span>
              </span>
              <span class="mx-3">-</span>
              <span>
                <template v-if="campaign.endDate">{{ campaign.endDate | formatDate('Do MMM YYYY, HH:mm') }}</template>
                <span v-else class="font-italic">{{ $t('general.none') }}</span>
              </span>
            </div>
            <div v-else>{{ campaignLengthInWeeks(campaign) }} weeks</div>
            <div class="d-flex justify-content-end align-items-center">
              <!-- b-btn
                v-if="isSuperAdmin"
                size="sm"
                @click="openExportModal(campaign)"
                variant="link"
              >Export</b-btn -->
              <b-btn
                v-if="campaign.preferences && campaign.preferences.hasRelativeDates && canDuplicateCampaign"
                size="sm"
                @click="openDuplicateModal(campaign)"
                variant="link"
              >Duplicate</b-btn>
              <b-btn
                v-if="!campaign.isDisabled"
                size="sm"
                :to="{ name: 'CampaignAnalytics', query: { campaignID: campaign.id } }"
                variant="link"
              >Analytics</b-btn>
              <b-btn
                class="ml-3 px-3"
                size="sm"
                :to="!campaign.isDisabled ?
                  { name: initialRouteName, params: { campaignID: campaign.id } } :
                  { name: 'EditCampaignGamification', params: { campaignID: campaign.id } }"
                variant="outline-primary"
                pill
              >
                <span v-if="!campaign.isDisabled">{{ $t('ctas.pencil-edit') }}</span>
                <span v-else>View</span>
              </b-btn>
            </div>
          </b-col>
        </b-row>

        <!-- modal to change the status of a campaign -->
        <b-modal
          v-if="campaign.status && statusMap[campaign.status]"
          :id="`status-modal-${campaign.id}`"
          title="Change Campaign Status"
          hide-footer
        >
          <b-select
            v-model="campaign.status"
            :options="statusOptionsToUpdate(campaign)"
            @change="updateCampaignStatus(campaign)"
          />
        </b-modal>
      </b-card>

      <b-row v-if="campaignsConnection && campaignsConnection.total">
        <b-col cols="12" class="d-flex justify-content-center align-items-middle">
          <b-pagination :per-page="filterLimit" :total-rows="campaignsConnection.total" v-model="filterPage"/>
        </b-col>
      </b-row>
    </div>

    <!-- campaign duplication modal -->
    <b-modal id="duplicate-campaign-modal" title="Duplicate Campaign" hide-footer>
      <b-alert
        v-if="!isCampaignDuplicationFormValid"
        variant="danger"
        class="mb-3"
        show
        dismissible
      >
        You have to set a start date and end date before publishing the campaign.
      </b-alert>
      <advanced-form
        autocomplete="off"
        layout="inline-md-3"
        submit-label="Duplicate Campaign"
        v-bind="campaignDuplicationFormFields"
        v-model="campaignDuplicationForm"
        @submit="duplicateCampaign()"
        :loading="copyCampaignLoading"
        :disbled="!isCampaignDuplicationFormValid || copyCampaignLoading"
      />
    </b-modal>
  </page>
</template>

<script>
import { mapRoot } from '@src/mixins/root';
import CampaignAdminListAllQuery from '@/graphql/queries/CampaignAdminListAll.gql';
import CampaignAdminCreateMutation from '@/graphql/mutations/CampaignAdminCreate.gql';
import UpdateCampaignMutation from '@/graphql/mutations/UpdateCampaign.gql';
import CampaignAdminDuplication from '@/graphql/queries/CampaignAdminDuplication.gql';
import CreateCampaignComponentMutation from '@/graphql/mutations/CreateCampaignComponent.gql';
import Page from '@/components/campaign-admin/page';
import AdvancedForm from '@src/components/AdvancedForm';

export const campaignStatusMap = {
  DRAFT: {
    label: 'Draft',
    colour: '#0ea5e9',
  },
  READY: {
    label: 'Ready',
    colour: '#06b6d4',
  },
  IN_PROGRESS: {
    label: 'Live',
    colour: '#10b981',
  },
  FINISHED: {
    label: 'Finished',
    colour: '#b8e994',
  },
  ARCHIVED: {
    label: 'Archived',
    colour: '#b8e994',
  },
};

export default {
  name: 'CampaignAdminCampaigns',
  components: {
    Page,
    AdvancedForm,
  },
  apollo: {
    campaignsConnection: {
      query: CampaignAdminListAllQuery,
      variables() {
        const {
          filterWhere: where,
          filterOrder: order,
          filterPage: page,
          filterLimit: limit,
        } = this;

        const titleLike = where.title_like && where.title_like.length > 1 ? where.title_like : null;
        const statusIn = where.status ? null : [ 'READY', 'IN_PROGRESS', 'DRAFT', 'FINISHED' ];
        const template = !where.template ? [ 'NO_TEMPLATE', 'TEMPLATE' ] : [ where.template ];
        return { where: Object.keys(where).length ? { ...where, title_like: titleLike, status_in: statusIn, template } : undefined, order, page, limit };
      },
      update({ company }) {
        const { campaignsConnection } = company;
        return campaignsConnection;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: this.$t('campaign-admin.errors.load-campaigns') });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  data: () => ({
    campaignToDuplicateID: null,
    isGlobalTemplate: false,
    campaignDuplicationForm: {},
    copyCampaignLoading: false,
    filterWhere: {
      status: null,
      title_like: null,
      template: null
    },
    filterPage: 1,
    filterLimit: 10,
    filterOrder: 'CREATED_DESC',
  }),
  computed: {
    ...mapRoot([ 'company', 'user', 'isRecruiter', 'isSuperAdmin' ]),
    isCampaignDuplicationFormValid() {
      const { startDate } = this.campaignDuplicationForm;
      return startDate;
    },
    initialRouteName: () => 'EditCampaignJobs',
    // @TODO Move this statusMap to the backend? Customisable status colours for customers?
    statusMap: () => campaignStatusMap,
    statusOptions() {
      return [
        { value: null, text: 'All Statuses' },
        ...Object.keys(this.statusMap).map(key => ({ value: key, text: this.statusMap[key].label }))
      ];
    },
    templateOptions() {
      const { isSuperAdmin } = this;
      const options = [
        { value: null, text: 'All Campaigns' },
        { value: 'TEMPLATE', text: 'Templates' },
      ];
      if (isSuperAdmin) {
        options.push({ value: 'GLOBAL_TEMPLATE', text: 'Real Links Templates' });
      }
      return options;
    },
    sortOptions() {
      return [
        { value: 'CREATED_DESC', text: 'Created (Newest First)' },
        { value: 'CREATED_ASC', text: 'Created (Oldest First)' },
        { value: 'UPDATED_DESC', text: 'Updated (Newest First)' },
        { value: 'UPDATED_ASC', text: 'Updated (Oldest First)' },
        { value: 'STARTDATE_DESC', text: 'Start Date (Newest First)' },
        { value: 'STARTDATE_ASC', text: 'Start Date (Oldest First)' },
        { value: 'ENDDATE_DESC', text: 'End Date (Newest First)' },
        { value: 'ENDDATE_ASC', text: 'End Date (Oldest First)' },
      ];
    },
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    canDuplicateCampaign() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('can-duplicate-campaign');
    },
    canHaveRelativeDates() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('relative-message-dates');
    },
    campaignDuplicationFormFields() {
      const { isGlobalTemplate } = this;
      const form = {
        title: {
          label: this.$t('content-fetch.labels.title'),
          type: 'STRING',
          helpText: this.$t('campaign-admin.hints.title')
        },
        startDate: {
          label: this.$t('content-fetch.labels.start-date'),
          placeholder: this.$t('campaign-admin.placeholders.end-date'),
          dateRange: { startDate: new Date() },
          helpText: this.$t('campaign-admin.hints.start-date'),
          required: true,
          type: 'DATETIME',
        },
        endDate: {
          label: this.$t('content-fetch.labels.end-date'),
          placeholder: this.$t('campaign-admin.placeholders.start-date'),
          helpText: this.$t('campaign-admin.hints.end-date'),
          type: 'DATETIME',
        },
        status: {
          label: this.$t('onboarding.slack.labels.status'),
          type: 'SELECT',
          required: true,
          choices: {
            DRAFT: this.$t('campaign-admin.labels.draft'),
            IN_PROGRESS: this.$t('campaign-admin.labels.ready'),
            ARCHIVED: this.$t('campaign-admin.labels.archived'),
          },
        },
        'preferences.canScheduleLinkedInPosts': {
          description: this.$t('campaign-admin.labels.schedule-linked-in'),
          type: 'CHECKBOX',
          default: true,
        },
        'preferences.showRecruitersOnLeaderboards': {
          description: this.$t('campaign-admin.labels.show-recruiters'),
          type: 'CHECKBOX',
          default: false,
        },
        ...(!isGlobalTemplate ? {
          copyJobs: {
            description: 'Copy selected jobs and filters',
            type: 'CHECKBOX',
            default: true,
          }
        } : {}),
        ...(!isGlobalTemplate ? {
          copyContent: {
            description: 'Copy selected pieces of content',
            type: 'CHECKBOX',
            default: true,
          }
        } : {}),
        copyGamification: {
          description: 'Copy leaderboard and collective goal',
          type: 'CHECKBOX',
          default: true,
        },
        ...(!isGlobalTemplate ? {
          copyParticipants: {
            description: 'Copy selected campaign participants and filters',
            type: 'CHECKBOX',
            default: true,
          },
        } : {}),
        copyEngagement: {
          description: 'Copy all engagement comms',
          type: 'CHECKBOX',
          default: true,
        },
      };

      return { form };
    },
  },
  methods: {
    statusOptionsToUpdate(campaign) {
      return Object.keys(this.statusMap)
        .map(key => ({ value: key, text: this.statusMap[key].label }))
        .filter(({ value }) => [ 'DRAFT', 'IN_PROGRESS', 'ARCHIVED' ].includes(value) || value === campaign.status);
    },
    campaignLengthInWeeks(campaign) {
      if (!campaign.startDate || !campaign.endDate) return 0;
      const startDate = new Date(campaign.startDate);
      const endDate = new Date(campaign.endDate);
      return Math.ceil((endDate.getTime() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
    },
    async createNewCampaign() {
      const create = {
        title: 'New Campaign',
        startDate: new Date((new Date()).getTime() + 7 * 24 * 60 * 60 * 1000),
        endDate: new Date((new Date()).getTime() + 5 * 7 * 24 * 60 * 60 * 1000),
        status: 'DRAFT',
        preferences: {
          canScheduleLinkedInPosts: true,
          showRecruitersOnLeaderboards: false,
          hasRelativeDates: this.canHaveRelativeDates,
        },
      };

      try {
        const { data } = await this.$apollo.mutate({
          mutation: CampaignAdminCreateMutation,
          variables: { create },
        });

        const { createCampaign: newCampaign } = data || {};
        this.$assert(newCampaign && newCampaign.id, 'Missing new campaign ID from mutation');
        this.$router.push({ name: this.initialRouteName, params: { campaignID: newCampaign.id }, query: { new: true } });
      } catch (err) {
        this.$notifyErr(err, { prepend: this.$t('campaign-admin.errors.create-error') });
      }
    },
    async updateCampaignStatus(campaign) {
      try {
        const { id: campaignID, status } = campaign;
        await this.$apollo.mutate({
          mutation: UpdateCampaignMutation,
          variables: {
            where: { id: campaignID },
            update: { status },
          },
        });
        this.$bvModal.hide(`status-modal-${campaignID}`);
        await this.$apollo.queries.campaignsConnection.refetch();
      } catch (err) {
        this.$notifyErr(err, { prepend: this.$t('campaign-admin.errors.update-status') });
      }
    },
    // openExportModal(campaign) {
    //   this.$bvModal.show('export-campaign-modal');
    //   this.exportCampaign = campaign;
    // },
    openDuplicateModal(campaign) {
      this.campaignToDuplicateID = campaign.id;
      this.$bvModal.show('duplicate-campaign-modal');
      console.log('campaign', campaign);
      const originalStartDate = campaign.startDate ? (campaign.startDate instanceof Date ? campaign.startDate : new Date(campaign.startDate)) : null;
      const daysToMoveOriginalDates = originalStartDate ? Math.floor((new Date().getTime() - originalStartDate.getTime()) / (24 * 60 * 60 * 1000)) : 0;
      const originalEndDate = campaign.endDate ? (campaign.endDate instanceof Date ? campaign.endDate : new Date(campaign.endDate)) : null;
      this.isGlobalTemplate = campaign.isGlobalTemplate;
      this.campaignDuplicationForm = {
        title: `${campaign.title} (copy)`,
        startDate: originalStartDate ? new Date(originalStartDate.getTime() + (7 + daysToMoveOriginalDates) * 24 * 60 * 60 * 1000) : null,
        endDate: originalEndDate ? new Date(originalEndDate.getTime() + (7 + daysToMoveOriginalDates) * 24 * 60 * 60 * 1000) : null,
        status: 'DRAFT',
        'preferences.canScheduleLinkedInPosts': true,
        'preferences.showRecruitersOnLeaderboards': false,
        copyJobs: !campaign.isGlobalTemplate,
        copyContent: !campaign.isGlobalTemplate,
        copyParticipants: !campaign.isGlobalTemplate,
        copyEngagement: true,
        copyGamification: true,
      };
    },
    async duplicateCampaign() {
      // @todo: Implement proper error handling
      this.copyCampaignLoading = true;
      try {
        const { campaignToDuplicateID, campaignDuplicationForm } = this;
        if (!campaignDuplicationForm.startDate) return;
        const { data: { company } } = await this.$apollo.query({
          query: CampaignAdminDuplication,
          variables: { campaignID: campaignToDuplicateID },
          fetchPolicy: 'network-only',
        });
        const { campaign } = company || {};
        if (!campaign || !campaign.startDate) return;
        const campaignStartDate = campaign.startDate instanceof Date ? campaign.startDate : new Date(campaign.startDate);
        const newCampaignStartDate = campaignDuplicationForm.startDate instanceof Date ? campaignDuplicationForm.startDate : new Date(campaignDuplicationForm.startDate);
        const daysToMoveDates = Math.floor((newCampaignStartDate.getTime() - campaignStartDate.getTime()) / (24 * 60 * 60 * 1000));
        const create = {
          title: campaignDuplicationForm.title,
          startDate: campaignDuplicationForm.startDate,
          endDate: campaignDuplicationForm.endDate,
          status: campaignDuplicationForm.status,
          preferences: {
            canScheduleLinkedInPosts: !!campaignDuplicationForm['preferences.canScheduleLinkedInPosts'],
            showRecruitersOnLeaderboards: !!campaignDuplicationForm['preferences.showRecruitersOnLeaderboards'],
            hasRelativeDates: true,
          },
        };

        if (campaignDuplicationForm.copyJobs && campaign.segment && campaign.segment.jobsConnection && campaign.segment.jobsConnection.where) {
          const jobsWhere = campaign.segment.jobsConnection.where;
          create.segment = {
            jobs: typeof jobsWhere === 'string' ? JSON.parse(jobsWhere) : jobsWhere,
          };
        }

        if (campaignDuplicationForm.copyParticipants && campaign.segment && campaign.segment.scimUsersConnection && campaign.segment.scimUsersConnection.where) {
          const scimUsersConnectionWhere = campaign.segment.scimUsersConnection.where;
          create.segment = {
            ...(create.segment || {}),
            scimUsers: typeof scimUsersConnectionWhere === 'string' ? JSON.parse(scimUsersConnectionWhere) : scimUsersConnectionWhere,
          };
        }

        if (campaignDuplicationForm.copyContent && Array.isArray(campaign.contentShares) && campaign.contentShares.length > 0) {
          create.contentShares = { id_in: campaign.contentShares.map(({ id }) => id) };
        }

        const { data } = await this.$apollo.mutate({
          mutation: CampaignAdminCreateMutation,
          variables: { create },
        });

        const { createCampaign: newCampaign } = data || {};

        const createNextEngagementComponent = async (parentID, newParentID, components) => {
          const currentComponent = components.find(({ teamsMessage, slackMessage, emailMessage }) => {
            const message = teamsMessage || slackMessage || emailMessage;
            return message && message.relativeSendAt && message.relativeSendAt.componentID === parentID;
          });
          if (!currentComponent) return;
          const { id: componentID, ...component } = currentComponent;
          const { teamsMessage, slackMessage, emailMessage } = component;
          const messageKey = teamsMessage ? 'teamsMessage' : slackMessage ? 'slackMessage' : emailMessage ? 'emailMessage' : null;
          delete component.__typename;
          delete component[messageKey].__typename;
          delete component[messageKey].relativeSendAt.__typename;
          if ((component[messageKey].job || component[messageKey].jobs) && campaignDuplicationForm.copyJobs) {
            if (component[messageKey].job) {
              delete component[messageKey].job.__typename;
            }
            if (Array.isArray(component[messageKey].jobs)) {
              component[messageKey].jobs.forEach(j => delete j.__typename);
            }
          } else if (component[messageKey].job) {
            delete component[messageKey].job;
          } else if (component[messageKey].jobs) {
            delete component[messageKey].jobs;
          }

          if ((component[messageKey].contentShare || component[messageKey].contentShares) && campaignDuplicationForm.copyContent) {
            if (component[messageKey].contentShare) {
              delete component[messageKey].contentShare.__typename;
            }
            if (Array.isArray(component[messageKey].contentShares)) {
              component[messageKey].contentShares.forEach(c => delete c.__typename);
            }
          } else if (component[messageKey].contentShare) {
            delete component[messageKey].contentShare;
          } else if (component[messageKey].contentShares) {
            delete component[messageKey].contentShares;
          }

          const sendAt = component[messageKey].sendAt instanceof Date ? component[messageKey].sendAt : new Date(component[messageKey].sendAt);
          const createComponent = {
            [messageKey]: {
              ...component[messageKey],
              relativeSendAt: {
                ...component[messageKey].relativeSendAt,
                componentID: newParentID,
              },
              sendAt: new Date(sendAt.getTime() + daysToMoveDates * 24 * 60 * 60 * 1000),
            },
          };

          console.log('start creating component', createComponent);
          const { data: { createCampaignComponent } } = await this.$apollo.mutate({
            mutation: CreateCampaignComponentMutation,
            variables: {
              campaign: { id: newCampaign.id },
              create: createComponent,
            },
          });
          if (!createCampaignComponent) {
            throw new Error('Failed to create component');
          };
          await createNextEngagementComponent(componentID, createCampaignComponent.id, components);
        };

        if (campaign && Array.isArray(campaign.components)) {
          create.components = [];
          const engagementComponents = campaign.components.filter(({ teamsMessage, slackMessage, emailMessage }) => teamsMessage || slackMessage || emailMessage);
          if (engagementComponents.length > 0 && campaignDuplicationForm.copyEngagement) {
            await createNextEngagementComponent(null, null, engagementComponents);
          }
          const createGamification = [];
          const leaderboardComponent = campaign.components.find(({ leaderboard }) => leaderboard);
          if (leaderboardComponent && campaignDuplicationForm.copyGamification) {
            const { leaderboard } = leaderboardComponent;
            delete leaderboard.__typename;
            if (leaderboard.prizes) {
              delete leaderboard.prizes.__typename;
              [ 'first', 'second', 'third' ].forEach(key => {
                if (leaderboard.prizes[key]) {
                  delete leaderboard.prizes[key].__typename;
                  if (leaderboard.prizes[key].image && !campaign.isGlobalTemplate) {
                    leaderboard.prizes[key].image = { id: leaderboard.prizes[key].image.id };
                  }
                }
              });
            }
            if (Array.isArray(leaderboard.scores)) {
              leaderboard.scores.forEach(score => {
                delete score.__typename;
              });
            }
            createGamification.push({ leaderboard });
          }
          const collectiveGoalComponent = campaign.components.find(({ collectiveGoal }) => collectiveGoal);
          if (collectiveGoalComponent && campaignDuplicationForm.copyGamification) {
            const { collectiveGoal } = collectiveGoalComponent;
            delete collectiveGoal.__typename;
            if (collectiveGoal.display) {
              delete collectiveGoal.display.__typename;
            }
            createGamification.push({ collectiveGoal });
          }

          if (createGamification.length > 0) {
            await Promise.all(createGamification.map(async newComponent => {
              const { data: { createCampaignComponent } } = await this.$apollo.mutate({
                mutation: CreateCampaignComponentMutation,
                variables: {
                  campaign: { id: newCampaign.id },
                  create: newComponent,
                },
              });
              if (!createCampaignComponent) {
                throw new Error('Failed to create gamification component');
              }
            }));
          }
        }

        this.$router.push({ name: this.initialRouteName, params: { campaignID: newCampaign.id } });
      } catch (error) {
        console.error('error', error);
        this.$notifyErr(error, { prepend: 'Failed to save duplicate component' });
      }
      this.copyCampaignLoading = false;
    },
  },
};
</script>

<style lang="scss">
.admin-campaign-list {
  .status-label {
    display: inline-block;
    color: #fff;
    font-weight: 500;
    font-size: 13px;
    padding: 4px 12px;
    border-radius: 4px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Campaigns.vue